import { get as getCookie, set as setCookie } from 'js-cookie';
import { useSnackbar } from 'notistack';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useLoggedInEmployeeId, useLoggedInUserRoles } from '~/components/Auth';
import { fetchOrganizationUnits } from '~/data/orgunits';
import { SURVEY_STATUS } from '~/data/surveys';
import { useSWR } from '~/hooks/useSWR';
import { AppDispatch } from '~/store';
import { Optional } from '~/types';

import { COOKIE_SURVEY_ANSWERED } from './constants';
import { getNotifications, moodSurveyAnswer } from './data';
import { SurveyNotification } from './types';

export type UseTopbarSurveyOutput = {
  notificationsLoading: boolean;
  failedToLoadNotifications: boolean;
  notifications: SurveyNotification[] | undefined;
  answerFeelingSurvey: (answer: number) => void;
  pulseAnswered: Optional<string>;
  anchorEl: Optional<HTMLDivElement>;
  getBadgeCount: () => number;
  open: boolean;
  handleSurveyOpen: (event: React.MouseEvent<HTMLDivElement>) => void;
  handleSurveyClose: () => void;
  handleAnswer: (surveyId: string) => void;
  handleResults: (surveyId: string) => void;
  resultId: Optional<string>;
  answerId: Optional<string>;
  handleAnswerClose: (isAnswerSubmission?: boolean) => void;
  handleResultsClose: () => void;
};

export const useTopbarSurvey = (): UseTopbarSurveyOutput => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch: AppDispatch = useDispatch();
  const myRoles = useLoggedInUserRoles();
  const myId = useLoggedInEmployeeId();
  const [pulseAnswered, setPulseAnswered] = useState(
    getCookie(COOKIE_SURVEY_ANSWERED),
  );

  const {
    loading: notificationsLoading,
    failedToLoad: failedToLoadNotifications,
    data: notifications,
    mutate: mutateNotifications,
  } = useSWR('getNotifications', getNotifications);

  const [answerId, setAnswerId] = useState<string>();
  const [resultId, setResultId] = useState<string>();
  const [anchorEl, setAnchorEl] = useState<Optional<HTMLDivElement>>(undefined);
  const open = Boolean(anchorEl);

  useEffect(() => {
    dispatch(fetchOrganizationUnits({ myRoles, myId }));
  }, [myRoles, myId, dispatch]);

  const handleSurveyOpen = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleSurveyClose = () => {
    setAnchorEl(undefined);
  };

  const getBadgeCount = () =>
    (notifications || []).filter(
      (notif) => notif.status === SURVEY_STATUS.ACTIVE,
    ).length;

  const handleAnswer = (surveyId: string) => {
    setAnswerId(surveyId);
  };

  const handleAnswerClose = (isAnswerSubmission?: boolean) => {
    setAnswerId(undefined);
    if (isAnswerSubmission) {
      mutateNotifications();
    }
  };

  const handleResults = (surveyId: string) => {
    setResultId(surveyId);
  };

  const handleResultsClose = () => {
    setResultId(undefined);
  };

  const answerFeelingSurvey = useCallback(
    (answer: number) => {
      (async () => {
        try {
          await moodSurveyAnswer(answer);
          const expDate = new Date();
          expDate.setTime(expDate.getTime() + 12 * 60 * 60 * 1000);

          setCookie(COOKIE_SURVEY_ANSWERED, '1', {
            path: '/',
            expires: expDate,
          });
          setPulseAnswered(getCookie(COOKIE_SURVEY_ANSWERED));
        } catch (err) {
          enqueueSnackbar(t('laFailedToSave'), {
            variant: 'error',
          });
        }
      })();
    },
    [enqueueSnackbar, t],
  );

  return {
    notificationsLoading,
    failedToLoadNotifications,
    notifications,
    answerFeelingSurvey,
    pulseAnswered,
    anchorEl,
    getBadgeCount,
    open,
    handleSurveyOpen,
    handleSurveyClose,
    handleAnswer,
    handleResults,
    resultId,
    answerId,
    handleAnswerClose,
    handleResultsClose,
  };
};
